import React, { useState, useEffect, useRef } from "react";
import {
  Layout,
  Menu,
  Card,
  Row,
  Col,
  Spin,
  Badge,
  Modal,
  Typography,
  Empty,
  Divider,
} from "antd";
import axios from "axios";
import "./LastChanceDeals.css";
import ReactGA from "react-ga4";
import ProductDetail from "../Product/ProductDetail";

const { Sider, Content } = Layout;
const { Title } = Typography;

const LastChanceDeals = ({ refreshCartCount }) => {
  const [productCategory, setProductCategory] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(false);

  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);

  const [siderCollapsed, setSiderCollapsed] = useState(false);
  const siderRef = useRef();

  // Helper to get selected category name
  const getSelectedCategoryName = () => {
    let name = "";
    productCategory.forEach((catGroup) => {
      const found = catGroup.data.find(
        (cat) => String(cat.ProductCategoryID) === String(selectedCategory)
      );
      if (found) name = found.CategoryName;
    });
    return name;
  };

  // Set document title on category or modal change
  useEffect(() => {
    if (showProductModal && selectedProductId) {
      // We'll set the title in the ProductDetail modal via a callback if needed
      // For now, keep the category title
      return;
    }
    const categoryName = getSelectedCategoryName();
    document.title = `JOJ | Last Chance Deal${
      categoryName ? " | " + categoryName : ""
    }`;
  }, [selectedCategory, showProductModal, selectedProductId, productCategory]);

  useEffect(() => {
    document.title = "JOJ | Last Chance Deal";
    fetchCategories();
    logUserActivity();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      fetchProducts(selectedCategory);
    }
    // eslint-disable-next-line
  }, [selectedCategory]);

  // Detect click outside for mobile menu auto-collapse
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        siderRef.current &&
        !siderRef.current.contains(event.target) &&
        window.innerWidth <= 768 &&
        !siderCollapsed
      ) {
        setSiderCollapsed(true);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [siderCollapsed]);

  const logUserActivity = async () => {
    const tokenString = localStorage.getItem("token");
    try {
      const jsondata = JSON.stringify({ ScreenID: "1002" });
      let config = {
        method: "post",
        url: global.config.APIURL.BaseURL + "api/UserActivity/InsertActivity",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + tokenString,
        },
        data: jsondata,
      };
      await axios.request(config);
    } catch (err) {}
  };

  const fetchCategories = async () => {
    const tokenString = localStorage.getItem("token");
    setLoading(true);
    try {
      let config = {
        method: "get",
        url:
          global.config.APIURL.BaseURL +
          "api/ProductCategory/ListofProductCategory",
        headers: {
          Authorization: "Bearer " + tokenString,
        },
      };
      const response = await axios.request(config);
      setProductCategory(response.data);
    } catch (err) {
      setProductCategory([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async (categoryIDQuery) => {
    let CategoryID = categoryIDQuery;
    if (!CategoryID) return;
    const tokenString = localStorage.getItem("token");
    setLoading(true);
    try {
      let config = {
        method: "get",
        url:
          global.config.APIURL.BaseURL +
          "/api/LastChanceDeal/GetListOfProductInDeal?CategoryID=" +
          CategoryID,
        headers: {
          Authorization: "Bearer " + tokenString,
        },
      };
      const response = await axios.request(config);
      setProducts(response.data);
    } catch (err) {
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Modal title logic
  useEffect(() => {
    if (showProductModal && selectedProductId) {
      // We'll set the title in ProductDetail if needed
    } else {
      const categoryName = getSelectedCategoryName();
      document.title = `JOJ | Last Chance Deal${
        categoryName ? " | " + categoryName : ""
      }`;
    }
    // eslint-disable-next-line
  }, [showProductModal, selectedProductId]);

  // Modal close handler
  const handleCancel = () => {
    setShowProductModal(false);
    setSelectedProductId(null);
    const categoryName = getSelectedCategoryName();
    document.title = `JOJ | Last Chance Deal${
      categoryName ? " | " + categoryName : ""
    }`;
  };

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {/* Left Side: Categories */}
      <Sider
        ref={siderRef}
        width={250}
        style={{ background: "#fff", borderRight: "1px solid #f0f0f0" }}
        breakpoint="md" // Collapse on medium screens and below
        collapsedWidth="0"
        collapsed={siderCollapsed}
        onCollapse={setSiderCollapsed}
      >
        <Menu
          mode="inline"
          selectedKeys={[selectedCategory]}
          onClick={(e) => {
            setSelectedCategory(e.key);
            fetchProducts(e.key);
            // Auto-collapse on mobile after selecting a category
            if (window.innerWidth <= 768) setSiderCollapsed(true);

            const IsEmployee = localStorage.getItem("IsEmployee");
            if (IsEmployee === "false") {
              ReactGA.event({
                category: "Deal Category",
                action: "clicked on Deal Category",
                label: e.key, // optional
                value: e.key, // optional, must be a number
                nonInteraction: false, // optional, true/false
                transport: "xhr", // optional, beacon/xhr/image
              });
            }
          }}
          style={{ height: "100%", borderRight: 0 }}
        >
          {productCategory.map((category) =>
            category.data.map((cat) => (
              <Menu.Item key={cat.ProductCategoryID}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <img
                    src={cat.CategoryImage}
                    alt={cat.CategoryName}
                    style={{
                      width: "30px",
                      height: "30px",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                  <span>{cat.CategoryName}</span>
                </div>
              </Menu.Item>
            ))
          )}
        </Menu>
      </Sider>

      {/* Right Side: Products */}
      <Layout>
        <Content style={{ padding: "24px", background: "#fff" }}>
          <div style={{ marginBottom: "28px", marginTop: "0px" }}>
            <Divider>
              <Title level={3} style={{ marginBottom: 0, marginTop: 0 }}>
                {getSelectedCategoryName() || "Products"}
              </Title>
            </Divider>
          </div>
          <Spin spinning={loading}>
            {products.length === 0 ? (
              <Empty
                description="No products found in this category."
                style={{ margin: "40px 0" }}
              />
            ) : (
              <Row gutter={[16, 16]}>
                {products.map((product) => (
                  <Col xs={24} sm={12} md={8} lg={6} key={product.ProductID}>
                    {product.DealRate < product.SalesRate &&
                    product.DealRate !== product.SalesRate ? (
                      <Badge.Ribbon text="Price Drop" color="green">
                        <Card
                          onClick={() => {
                            setSelectedProductId(product.ProductID);
                            setShowProductModal(true);
                          }}
                          className="equal-height-card"
                          hoverable
                          cover={
                            <div className="equal-image-container">
                              <img
                                alt={product.ProductName}
                                src={product.ImageUrl}
                                className="equal-image"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedProductId(product.ProductID);
                                  setShowProductModal(true);
                                  const IsEmployee =
                                    localStorage.getItem("IsEmployee");
                                  if (IsEmployee === "false") {
                                    ReactGA.event({
                                      category: "Deal Product",
                                      action: "clicked on Deal Product",
                                      label: product.DesignNo, // optional
                                      value: product.ProductID, // optional, must be a number
                                      nonInteraction: false, // optional, true/false
                                      transport: "xhr", // optional, beacon/xhr/image
                                    });
                                  }
                                }}
                              />
                            </div>
                          }
                        >
                          <Card.Meta
                            title={product.DesignNo}
                            description={
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                }}
                              >
                                <span
                                  style={{
                                    textDecoration: "line-through",
                                    color: "#888",
                                  }}
                                >
                                  ₹ {product.SalesRate.toFixed(2)}
                                </span>
                                <span
                                  style={{ color: "green", fontWeight: "bold" }}
                                >
                                  ₹ {product.DealRate.toFixed(2)}
                                </span>
                              </div>
                            }
                          />
                        </Card>
                      </Badge.Ribbon>
                    ) : (
                      <Card
                        onClick={() => {
                          setSelectedProductId(product.ProductID);
                          setShowProductModal(true);
                        }}
                        hoverable
                        className="equal-height-card"
                        cover={
                          <div className="equal-image-container">
                            <img
                              alt={product.ProductName}
                              src={product.ImageUrl}
                              className="equal-image"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedProductId(product.ProductID);
                                setShowProductModal(true);
                              }}
                            />
                          </div>
                        }
                      >
                        <Card.Meta
                          title={product.DesignNo}
                          description={`₹ ${product.SalesRate.toFixed(2)}`}
                        />
                      </Card>
                    )}
                  </Col>
                ))}
              </Row>
            )}
          </Spin>
        </Content>
      </Layout>

      {/* Modal for Product Details */}
      <Modal
        open={showProductModal}
        onCancel={handleCancel}
        footer={null}
        width="90%"
        style={{ maxWidth: 900 }}
        destroyOnClose
        styles={{ padding: 0 }}
        afterClose={() => {
          // Reset title after modal closes
          const categoryName = getSelectedCategoryName();
          document.title = `JOJ | Last Chance Deal${
            categoryName ? " | " + categoryName : ""
          }`;
        }}
      >
        {showProductModal && selectedProductId && (
          <ProductDetail
            refreshCartCount={refreshCartCount}
            productIdQuery={selectedProductId}
            isModal
            setDocumentTitle={(title) => {
              document.title = title;
            }}
          />
        )}
      </Modal>
    </Layout>
  );
};

export default LastChanceDeals;
